import React, { useEffect, useState, useRef } from "react";
import {
    RESOURCE_NO_RESULTS_TEXT,
    RESOURCE_PAGE_SIZE,
    RESOURCE_RESET_FILTERS_TEXT,
    RESOURCE_RESET_ALL_FILTERS,
    RESOURCE_TERM_INDUSTRIES,
    RESOURCE_TERM_PRODUCT_INTERESTS,
    RESOURCE_TERM_RESOURCE_TYPE,
    RESOURCE_TERM_TOPIC,
    resourceCategoryFieldMapping,
    RESOURCE_RESULTS_ERROR,
    RESOURCE_KEYWORD_PLACEHOLDER,
} from "@qlik/qlik/src/lib/constants/resource-constants";
import { useDebouncedCallback } from "@qlik/qlik/src/hooks/useDebouncedCallback";
import Pagination from "@qlik/qlik/src/components/Common/Pagination";
import { fetchResourceListing } from "@qlik/qlik/src/lib/services/resources/api";
import { resourceMapper } from "@qlik/qlik/src/lib/util/mappers/contentful-response-mappers";
import NoResults from "@qlik/qlik/src/components/Common/NoResults";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import {
    getValidatedCategoryFilters,
    scrollToElement,
    getMicrocopyValueFromKeys,
} from "@qlik/qlik/src/lib/util/helpers";
import FilterChips from "@qlik/qlik/src/components/Common/FilterChips";
import ResourceCategoryData from "@qlik/qlik/src/graphql/queries/resources";
import useUrlQueryParamsState from "@qlik/qlik/src/hooks/useUrlQueryParamsState";
import DynamicCheckboxFilterGroup from "@qlik/qlik/src/components/Common/DynamicCheckboxFilterGroup";
import MultiCtaResourceCard from "./MultiCtaResourceCard";
import SingleCtaResourceCard from "./SingleCtaResourceCard";
import { useI18n } from "@qlik/utils/i18nContext";
import useSiteMetadata from "@qlik/qlik/src/hooks/useSiteMetaData";

/**
 * Listing component for Resources with filters & pagination
 * @returns
 */
const ResourceListing = ({ data }) => {
    const { additionalConfiguration, backgroundColor, customId } = data || {};
    const [listing, setListing] = useState([]); // filtered resources
    const [total, setTotal] = useState(0); // total items availabel for the current query
    const [categories, setCategories] = useState([]); // set of filter categories - static
    const [currentCategory, setCurrentCategory] = useState(""); // keeps track of the last filtered category
    const [isLoading, setIsLoading] = useState(true);

    const { i18n } = useI18n();

    // The contentful env and preview status used for this build, to be passed to the Netlify function so that we query resources for the current environment.
    const { env, preview } = useSiteMetadata();

    const initialFilters = {
        page: 0,
        limit: RESOURCE_PAGE_SIZE,
        query: "",
        [RESOURCE_TERM_PRODUCT_INTERESTS]: [],
        [RESOURCE_TERM_INDUSTRIES]: [],
        [RESOURCE_TERM_RESOURCE_TYPE]: [],
        [RESOURCE_TERM_TOPIC]: [],
    };
    const [filters, setFilters] = useUrlQueryParamsState(initialFilters, resourceCategoryFieldMapping);

    const [
        resetFilterText,
        resetAllFilterText,
        noResultsText,
        searchText,
    ] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        RESOURCE_RESET_FILTERS_TEXT,
        RESOURCE_RESET_ALL_FILTERS,
        RESOURCE_NO_RESULTS_TEXT,
        RESOURCE_KEYWORD_PLACEHOLDER,
    ]);

    // get all the available resource category terms at build time
    // const { allCategoryData } = ResourceCategoryData(i18n.curr.langCode);
    const { allCategoryData, categoryTerms } = ResourceCategoryData(i18n.curr.enLangName);

    // create a debounced version API call for fetching resource entries for current filters
    // only state dependancy is filters state
    const fetchResources = useDebouncedCallback(async () => {
        try {
            const results = await fetchResourceListing(
                filters,
                env,
                i18n.curr.enLangName,
                i18n.curr.contentfulCode,
                preview
            );
            if (!results) throw new Error(RESOURCE_RESULTS_ERROR);
            setTotal(results.results?.total);
            setListing(resourceMapper(results.results?.items));
            setIsLoading(false);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    });

    // set category filters with validations
    useEffect(() => {
        // construct category objects
        const resourceCategoryData = Object.entries(allCategoryData).map(([categoryName, categoryFilters]) => {
            const transformedCategoryName = categoryName.replace("_", " ");

            const categoryObj = {
                id: transformedCategoryName,
                title: categoryTerms?.terms?.find((el) => el?.name?.includes(transformedCategoryName))?.title,
                subTerms: [],
            };

            // construct category filter items for each category
            categoryObj.subTerms = categoryFilters.map(({ fieldValue, edges }) => ({
                id: fieldValue,
                title: fieldValue,
                isDisabled: false,
                items: edges?.map((item) => item.node.id), // keep the resource items for each category to update filters availability
            }));

            // sort category filters in asc
            categoryObj.subTerms.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

            return categoryObj;
        });

        const validatedCategories = getValidatedCategoryFilters(
            { id: currentCategory, value: filters[currentCategory] },
            resourceCategoryData
        );

        setCategories(validatedCategories);
    }, [filters, currentCategory, allCategoryData]);

    // fetch resource listing as per the filters applied
    useEffect(() => {
        const updateListing = async () => {
            setIsLoading(true);
            await fetchResources();
        };

        updateListing();
    }, [filters, fetchResources]);

    // Handle search query updates
    const handleQueryChange = (event) => {
        event.preventDefault();
        const query = event?.target?.value;
        // contentful will only return results for queries more than 2 characters long
        setFilters((prev) => ({ ...prev, page: 0, query }));
    };

    const sectionRef = useRef(null);
    const handlePageChange = (selectedPage) => {
        setFilters((prev) => ({ ...prev, page: selectedPage }));
        scrollToElement(sectionRef);
    };

    const {
        sectionColorMapping: { bgColor },
    } = getColorClassName(backgroundColor);
    const spacingClassName = getSpacingClassName(additionalConfiguration, "py-15");

    // filters which are reflected in filter chips
    // excluding page & limit
    const getFilterChipFilters = () => {
        const filtersObj = { ...filters };
        delete filtersObj.page;
        delete filtersObj.limit;
        return filtersObj;
    };

    // update filter state when filter chip close
    const handleFilterChipClose = (filterCategory, filterValue) => {
        const currentFilterValue = filters[filterCategory];
        let updatedFilter = "";
        if (typeof currentFilterValue !== "string") {
            updatedFilter = currentFilterValue?.filter((filter) => filter !== filterValue);
        }
        setCurrentCategory(filterCategory);
        setFilters((prev) => ({ ...prev, [filterCategory]: updatedFilter }));
    };

    const resetFilters = () => {
        setCurrentCategory("");
        setFilters({
            page: 0,
            limit: RESOURCE_PAGE_SIZE,
            query: "",
            ...Object.fromEntries(Object.keys(resourceCategoryFieldMapping).map((key) => [[key], []])),
        });
    };

    /**
     * Render Resource card based on CTAs available
     * @param {Object} resource - Resource data
     * @returns
     */
    const renderResourceCard = (resource) =>
        resource.cta?.length && resource.cta?.length > 1 ? (
            <MultiCtaResourceCard key={resource.id} resource={resource} />
        ) : (
            <SingleCtaResourceCard key={resource.id} resource={resource} />
        );

    return (
        <section ref={sectionRef} className={`${bgColor} ${spacingClassName} scroll-m-20`} id={customId}>
            <div className="container">
                <div className="md:row">
                    <div className="md:col-4 xl:col-3">
                        <div>
                            <input
                                className="body w-full h-15 py-4 pl-15 pr-5 mb-7.75 border border-stone bg-filterSearch bg-no-repeat bg-filter-search-icon rounded placeholder:text-gray"
                                type="text"
                                placeholder={searchText}
                                value={filters.query}
                                onChange={handleQueryChange}
                            />
                            {/* Render filters for each top level category */}
                            {categories?.map(
                                (category, i) =>
                                    category.subTerms && (
                                        <DynamicCheckboxFilterGroup
                                            key={category.id}
                                            category={category}
                                            parentCategory={category.title}
                                            categoryData={category.subTerms}
                                            filters={filters}
                                            setFilters={setFilters}
                                            setCurrentCategory={setCurrentCategory}
                                            index={i}
                                        />
                                    )
                            )}
                            {Object.values(getFilterChipFilters())
                                .flat()
                                .filter(Boolean).length > 0 && (
                                <button
                                    className="hidden md:inline-block mt-8.75 text-xs lg:text-base text-green tracking-wide4 cursor-pointer uppercase"
                                    onClick={resetFilters}
                                >
                                    <span className="inline-block w-3 h-3 mr-2.5 bg-closeIconGreenMd bg-no-repeat" />
                                    {resetAllFilterText}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="md:col-8 xl:col-9 xl:pl-11.25">
                        {Object.values(getFilterChipFilters())
                            .flat()
                            .filter(Boolean).length > 0 && (
                            <div className="flex flex-wrap gap-2.5 pb-7.5 lg:pb-15 pt-5 sm:pt-0">
                                <FilterChips
                                    filters={getFilterChipFilters()}
                                    onClose={handleFilterChipClose}
                                    onResetFilters={resetFilters}
                                    resetFiltersLabel={resetFilterText}
                                />
                            </div>
                        )}
                        {!isLoading && listing?.length === 0 && (
                            <div className="flex h-full items-center">
                                <NoResults className="w-full text-center text-stone font-medium" text={noResultsText} />
                            </div>
                        )}
                        <div>
                            <div
                                className={
                                    // (isLoading ? "blur-sm " : "") +
                                    "transition grid gap-3.75 md:grid-cols-2 xl:grid-cols-3 lg:gap-7.5"
                                }
                            >
                                {listing?.map((resource, i) => (
                                    <React.Fragment key={i}>{renderResourceCard(resource)}</React.Fragment>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Pagination
                                currentPage={filters.page}
                                limit={filters.limit}
                                total={total}
                                handlePageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ResourceListing;
